import React, { useMemo } from "react";

/**
 * RACE LOGIC canon speedometer (v2.1) - black needle gauge, no numbers.
 * A north-half semicircle with white tick marks (major + minor) and a needle that
 * rises with live CPS (speed). The gauge GLOWS with the accurate streak
 * (5 yellow · 10 green · 17+ vibrant green); any mistake clears the glow (streak resets upstream).
 *
 * Props:
 *   cps               live characters-per-second (drives the needle)
 *   accurateStreak    consecutive accurate chars since last mistake (drives the glow)
 *   maxCps            CPS mapped to full-scale (needle hard right). Default 6.
 */
const CX = 100, CY = 100;
const R_OUT = 92, R_MID = 60, R_HUB = 30;

function pt(r, deg) {
  const rad = (deg * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY - r * Math.sin(rad)];
}

export const Speedometer = ({ cps = 0, accurateStreak = 0, maxCps = 6 }) => {
  const frac = Math.max(0, Math.min(1, (Number(cps) || 0) / maxCps));
  const needleDeg = 180 - frac * 180;
  const [nx, ny] = pt(66, needleDeg);

  const ticks = useMemo(() => {
    const N = 40;
    const arr = [];
    for (let i = 0; i <= N; i++) {
      const deg = 180 - (i / N) * 180;
      const major = i % 8 === 0;
      const [x1, y1] = pt(major ? R_OUT - 2 : R_OUT - 3, deg);
      const [x2, y2] = pt(major ? 68 : 77, deg);
      arr.push({ x1, y1, x2, y2, w: major ? 3.4 : 1.7 });
    }
    return arr;
  }, []);

  const glow = useMemo(() => {
    const s = Number(accurateStreak) || 0;
    if (s >= 17) return { color: "#39FF14", blur: 18 };
    if (s >= 10) return { color: "#22c55e", blur: 11 };
    if (s >= 5) return { color: "#facc15", blur: 8 };
    return { color: "transparent", blur: 0 };
  }, [accurateStreak]);

  return (
    <svg
      viewBox="0 0 200 108"
      className="w-28 h-[60px] transition-[filter] duration-200"
      style={{ filter: glow.blur ? `drop-shadow(0 0 ${glow.blur}px ${glow.color})` : "none" }}
      data-testid="race-speedometer"
      role="img"
      aria-label="Speedometer"
    >
      {/* Black outer half-disc */}
      <path d={`M ${CX - R_OUT} ${CY} A ${R_OUT} ${R_OUT} 0 0 1 ${CX + R_OUT} ${CY} Z`} fill="#0b0b0b" />
      {/* Carve the interior so only the outer band remains black */}
      <path d={`M ${CX - R_MID} ${CY} A ${R_MID} ${R_MID} 0 0 1 ${CX + R_MID} ${CY} Z`} fill="#ffffff" />
      {/* White tick marks on the black band */}
      {ticks.map((t, i) => (
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke="#ffffff" strokeWidth={t.w} strokeLinecap="butt" />
      ))}
      {/* Black center hub */}
      <path d={`M ${CX - R_HUB} ${CY} A ${R_HUB} ${R_HUB} 0 0 1 ${CX + R_HUB} ${CY} Z`} fill="#0b0b0b" />
      {/* Needle */}
      <line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#0b0b0b" strokeWidth="4.5" strokeLinecap="round" />
      {/* Pivot ring: white ring with a dark hole */}
      <circle cx={CX} cy={CY} r="8" fill="#ffffff" />
      <circle cx={CX} cy={CY} r="3.5" fill="#0b0b0b" />
    </svg>
  );
};

export default Speedometer;
